import {jsx} from 'slate-hyperscript'

const deserialize = (el, markAttributes = {}) => {
    if (el.nodeType === Node.TEXT_NODE) {
        return jsx('text', markAttributes, el.textContent)
    } else if (el.nodeType !== Node.ELEMENT_NODE) {
        return null
    }

    const nodeAttributes = {...markAttributes}

    // define attributes for text nodes
    switch (el.nodeName) {
        case 'STRONG':
            nodeAttributes.bold = true;
            break;
        case 'EM':
            nodeAttributes.italic = true;
            break;
        case 'S':
            nodeAttributes.strikethrough = true;
            break;
        case 'U':
            nodeAttributes.underline = true;
            break;
        case 'CODE':
            nodeAttributes.code = true;
            break;
        case 'MARK':
            nodeAttributes.highlight = true;
    }

    const children = Array.from(el.childNodes)
        .map(node => deserialize(node, nodeAttributes))
        .flat()

    if (children.length === 0) {
        children.push(jsx('text', nodeAttributes, ''))
    }

    switch (el.nodeName) {
        case 'BODY':
            return jsx('fragment', {}, children)
        case 'BR':
            return '\n'
        case 'HR':
            return jsx('element', {type: 'divider'}, children)
        case 'BLOCKQUOTE':
            return jsx('element', {type: 'quote', align: el.getAttribute('align')}, children)
        case 'P':
            return jsx('element', {type: 'paragraph', align: el.getAttribute('align')}, children)
        case 'H1':
            return jsx('element', {type: 'heading-one', align: el.getAttribute('align')}, children)
        case 'H2':
            return jsx('element', {type: 'heading-two', align: el.getAttribute('align')}, children)
        case 'H3':
            return jsx('element', {type: 'heading-three', align: el.getAttribute('align')}, children)
        case 'UL':
            return jsx('element', {type: 'bulleted-list', align: el.getAttribute('align')}, children)
        case 'OL':
            return jsx('element', {type: 'numbered-list', align: el.getAttribute('align')}, children)
        case 'LI':
            return jsx('element', {type: 'list-item'}, children)
        case 'A':
            return jsx(
                'element',
                {type: 'link', url: el.getAttribute('href')},
                children
            )
        default:
            return children
    }
}

const LIST_TYPES = ['bulleted-list', 'numbered-list']

/*
 * A newline inside a single block keeps two visual rows in one node, and every block level format
 * (H1/H2/quote/list/align) applies to a whole block - so one H1 would format every row at once.
 * Stored content carries those newlines from <br> tags and from legacy plain text, so split each
 * block into one block per row on the way in. Rows the user can see are rows the user can format.
 */
const splitRowsIntoBlocks = node => {
    if (!node.children) {
        return [node]
    }

    // Lists hold list items, not text - split the items instead.
    if (LIST_TYPES.includes(node.type)) {
        return [{...node, children: node.children.flatMap(splitRowsIntoBlocks)}]
    }

    const rows = [[]]
    node.children.forEach(child => {
        if (typeof child.text === 'string' && child.text.includes('\n')) {
            child.text.split('\n').forEach((text, i) => {
                if (i > 0) rows.push([])
                rows[rows.length - 1].push({...child, text})
            })
        } else {
            rows[rows.length - 1].push(child)
        }
    })

    if (rows.length === 1) {
        return [node]
    }

    return rows.map(children => ({...node, children: children.length ? children : [{text: ''}]}))
}

export default function slateHTMLToValue(html) {
    if (!html) {
        return null;
    }

    if (html.charAt(0) !== '<') { // wrap with P tag if it's a string only (e.g. old data)
        html = "<p>" + html + "</p>";
    }

    const document = new DOMParser().parseFromString(html, 'text/html')

    return deserialize(document.body).flatMap(splitRowsIntoBlocks)
}